import { useTranslation } from "react-i18next";
import {
  footerButtonHeight,
  GhostButton,
  PrimaryButton,
} from "../../modalPrimitives";

interface ItemDiscountsFooterProps {
  /** Сумата по менюто — без никаква корекция. */
  totalBefore: number;
  /** Сумата, която ще се плати след записа. */
  totalAfter: number;
  canSave: boolean;
  saving: boolean;
  onBack: () => void;
  onSave: () => void;
  isNarrow: boolean;
  isDesktop: boolean;
}

/** Футърът: „Отстъпка −X" и „~~преди~~ → след €" вляво, „Назад" и
 *  „Запази {след}" вдясно. На телефон обобщението е на собствен ред отгоре. */
export const ItemDiscountsFooter = ({
  totalBefore,
  totalAfter,
  canSave,
  saving,
  onBack,
  onSave,
  isNarrow,
  isDesktop,
}: ItemDiscountsFooterProps) => {
  const { t } = useTranslation();
  const discountAmount = totalBefore - totalAfter;
  const hasDiscount = discountAmount >= 0.005;
  const buttonHeight = footerButtonHeight(isNarrow);

  const summary = (
    <div
      className={`flex min-w-0 ${
        isNarrow
          ? "items-baseline justify-between gap-3"
          : "flex-col justify-center gap-0.5"
      }`}
    >
      <span className="flex items-baseline gap-2">
        <span className="text-[12.5px] font-semibold text-pos-soft2">
          {t("itemDiscounts.discountLabel")}
        </span>
        <span
          className={`font-pos-mono text-[20px] font-bold leading-[1.2] tabular-nums ${
            hasDiscount ? "text-pos-disc-ink" : "text-pos-muted"
          }`}
        >
          {hasDiscount ? `−${discountAmount.toFixed(2)}` : "0.00"}
        </span>
      </span>
      <span className="whitespace-nowrap font-pos-mono text-[12.5px] tabular-nums text-pos-soft2">
        {hasDiscount && (
          <>
            <span className="text-pos-muted line-through">
              {totalBefore.toFixed(2)}
            </span>
            {" → "}
          </>
        )}
        <span className="font-bold text-pos-ink">{totalAfter.toFixed(2)}</span> €
      </span>
    </div>
  );

  const buttons = (
    <>
      <GhostButton
        label={t("common.back")}
        onClick={onBack}
        shortcut={isDesktop ? "Esc" : undefined}
        height={buttonHeight}
      />
      <PrimaryButton
        label={
          <>
            {t("common.save")}{" "}
            <span className="font-pos-mono">{totalAfter.toFixed(2)}</span>
          </>
        }
        onClick={onSave}
        disabled={!canSave}
        loading={saving}
        shortcut={isDesktop ? "⏎" : undefined}
        height={buttonHeight}
      />
    </>
  );

  if (isNarrow) {
    return (
      <div className="flex flex-col gap-2.5 border-t border-pos-line2 bg-pos-card px-4 py-3">
        {summary}
        <div className="grid grid-cols-[1fr_2fr] gap-2">{buttons}</div>
      </div>
    );
  }

  return (
    <div className="grid grid-cols-[minmax(0,1fr)_minmax(0,0.5fr)_minmax(0,1fr)] items-center gap-2.5 border-t border-pos-line2 bg-pos-card px-[22px] py-3.5">
      {summary}
      {buttons}
    </div>
  );
};
